import React, { createRef, useCallback, useEffect, useState } from "react";
import { Button, Modal } from "@aqar-app/react-ui";
import dayjs from "dayjs";
import dynamic from "next/dynamic";
import { Icon } from "@/components/icon";
import BreadCrumb from "../../components/new-resolver/breadCrumb";
import { useFindDailyListings } from "@/components/new-resolver/useFindListings";
import css from "./.module.scss";
import { usePath } from "@/contexts/path/path";
import { ListingHolder } from "@/containers/UserListings/UserListings";
import { DailyListingScreen } from "@/components/Listing/DailyListingScreen";
import {
  CategoryList,
  CountersButtons,
} from "@/components/new-resolver/CountersButtons";
import { Filters } from "@/components/filters/filters";
import ListingResolver from "../../lib/path-resolver-middlewares/listing";
import { District } from "@/lib/path-resolver-middlewares/district";
import { Direction } from "@/lib/path-resolver-middlewares/direction";
import { City } from "@/lib/path-resolver-middlewares/city";
import { Category } from "@/lib/path-resolver-middlewares/category";
import { Filter } from "@/lib/path-resolver-middlewares/filters";
import { InitialResolver } from "@/lib/use.middlewares";
import Title from "../../components/Title";
import AppLayout from "../../containers/layouts/AppLayout";
import { Listing } from "@/components/Listing/ListingCard";
import { ListPagination, NoListingsFound } from "../[[...params]]";
import {
  listingToEECItemObject,
  trackSelectItem,
  trackViewItemList,
  trackViewProperties,
} from "@/lib/tracking/trackEECEvents";
import { SpecialDailyList } from "@/components/Listing/Special";
import { trackSelectDMRItem } from "@/lib/tracking/trackDMREvents";

const DailyDatePickerPopup = dynamic(
  () =>
    import("../../components/Listing/DailyListingScreen/datepicker").then(
      (m) => m.DailyDatePickerPopup,
    ),
  { ssr: false },
);

const arr = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
];

function ListingsHolder() {
  return arr.map((k) => <ListingHolder key={k} />);
}

function DailyListingsScreen({ setFrozenContext }) {
  const path = usePath();
  const {
    district,
    daily_renting_filter,
    category,
    categoriesDictionary,
    city,
    direction,
    filters,
    previousPath,
  } = path;

  const [isExpand, setIsExpand] = useState(true);
  const bottomCountersButtons = createRef();
  const scrollToBottomCounters = useCallback(() => {
    setIsExpand(true);
    if (bottomCountersButtons?.current) {
      bottomCountersButtons.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [bottomCountersButtons]);
  useEffect(() => {
    if (window.collapsed) return;
    setIsExpand(false);
    window.collapsed = true;
  }, []);
  const [listings, { loading, total, sov }] = useFindDailyListings({}, () => {
    trackViewItemList({
      itemListName: "daily rent listings",
      listingType: "daily",
      category: category?.en,
      cityName: city?.name_en,
      directionName: direction?.name_en,
      districtName: district?.name_en,
      filters,
      count: total,
      listings: (listings || []).map((listing, index) =>
        listingToEECItemObject(listing, index, categoriesDictionary),
      ),
    });
    trackViewProperties({
      listingType: "daily",
      viewType: "list",
    });
  });
  useEffect(() => {
    if (path.listing) return;

    setFrozenContext(path);
  }, [path]);
  return (
    <div>
      <Title />
      <CategoryList />
      <div className={css.container}>
        <div className={css.breadcrumb}>
          <BreadCrumb />
          <Button
            onClick={Modal.open(<DailyDatePickerPopup />)}
            outline
            theme="info"
          >
            {daily_renting_filter?.day_date ? (
              <>
                <Icon width={15} height={15} name="close-bordered-red" />

                <span>
                  {dayjs(daily_renting_filter.day_date.gte).format("DD MMM")}-
                  {dayjs(daily_renting_filter.day_date.lte).format("DD MMM")}
                </span>
              </>
            ) : (
              <>
                <Icon width={15} height={15} name="dailyRent-colored" />

                <span>اختر تاريخ الوصول و الخروج...</span>
              </>
            )}
          </Button>
        </div>
        {!district && category && category.id !== 0 && (
          <CountersButtons
            isExpand={isExpand}
            onExpand={scrollToBottomCounters}
          />
        )}
        {city && <SpecialDailyList listings={sov?.listings || []} />}

        <div>
          {loading ? (
            <ListingsHolder />
          ) : (
            listings.map((listing, index) => (
              <Listing
                key={listing.id}
                listing={listing}
                index={index}
                preloadImage={index < 4}
                trackClickEvent={() => {
                  trackSelectItem({
                    itemListName: "daily rent listings",
                    listingType: "daily",
                    category: category?.en,
                    cityName: city?.name_en,
                    directionName: direction?.name_en,
                    districtName: district?.name_en,
                    filters: {
                      ...filters,
                      start_date: daily_renting_filter?.day_date?.gte,
                      end_date: daily_renting_filter?.day_date?.lte,
                    },
                    count: total,
                    listings: [listing].map((listing, index) =>
                      listingToEECItemObject(
                        listing,
                        index,
                        categoriesDictionary,
                      ),
                    ),
                  });
                  trackSelectDMRItem({
                    source: previousPath || encodeURI(document.referrer),
                    listing: listing,
                    filters,
                    categoriesDictionary,
                  });
                }}
              />
            ))
          )}
        </div>
        {!loading && !listings.length ? <NoListingsFound /> : null}
        <ListPagination total={total} loading={loading} />
        {!district && category && category.id !== 0 && (
          <div ref={bottomCountersButtons}>
            <CountersButtons
              isExpand={isExpand}
              onExpand={scrollToBottomCounters}
              bottom
            />
          </div>
        )}
      </div>
    </div>
  );
}

function DailyRentingPage() {
  const { updateContext, categories, ...path } = usePath();
  const { listing } = path;
  const [frozenContext, setFrozenContext] = useState(path);
  //useResolvePathForBackAndForward();

  if (listing) return <DailyListingScreen frozenContext={frozenContext} />;
  return <DailyListingsScreen setFrozenContext={setFrozenContext} />;
}

DailyRentingPage.getInitialProps = InitialResolver(
  /* withServerOnly: insures it only run on SSR */

  /* Category.Resolve: Resolves if path=/category/page? */
  Filter.Resolve,
  Filter.Daily.Resolve,
  Category.Resolve,
  /* City.Resolve: Resolves if path=/category/city/page? */
  City.Resolve,

  /* Direction.Resolve: Resolves if path=/category/city/direction/page? */
  Direction.Resolve,

  /* District.Resolve: Resolves if path=/category/riyadhOrJeddah/direction/district/page?
   * District.Resolve: Resolves if path=/category/city/district/page?
   * */
  District.Resolve,

  /* Listing.Resolve: Resolves if path=/category/riyadhOrJeddah/direction/district/listing
   * Listing.Resolve: Resolves if path=/category/city/district/listing
   * */
  ListingResolver.Resolve,
  () => {
    return { path: "Last Middleware if none above resolved" };
  },
);
DailyRentingPage.getLayout = ({ children }) => (
  <AppLayout>
    <Filters.Layout>{children}</Filters.Layout>
  </AppLayout>
);

export default DailyRentingPage;
